import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Sun, Moon, Check, Loader2, UserRound } from 'lucide-react';

import apiClient from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { useProfileStore, type EmployeeProfile } from '../../store/profileStore';
import { PersonalDetailsForm } from '../../components/PersonalDetailsForm';
import { PageHeader } from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/Card';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Field } from '../../components/Field';
import { Separator } from '../../components/Separator';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/Tabs';
import { getUserAvatarUrl } from '../../utils/avatar';

function initials(name?: string) {
  if (!name) return 'U';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || 'U';
}

export function Profile() {
  const { user } = useAuthStore();
  const { theme, setTheme } = useUIStore();
  const { getSelf, saveSelf } = useProfileStore();
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getSelf()
      .then((p) => { if (!cancelled) setProfile(p); })
      .catch(() => undefined) // accounts with no employee record just show the basics below
      .finally(() => { if (!cancelled) setLoadingProfile(false); });
    return () => { cancelled = true; };
  }, [getSelf]);

  const saveDetails = async (patch: Record<string, any>) => {
    const updated = await saveSelf(patch);
    setProfile(updated);
    return updated;
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        icon={UserRound}
        title="Profile & settings"
        description="Manage your personal information, preferences, and security."
      />

      <Tabs defaultValue="general">
        <TabsList variant="underline" className="mb-6 w-full">
          <TabsTrigger variant="underline" value="general">General</TabsTrigger>
          <TabsTrigger variant="underline" value="preferences">Preferences</TabsTrigger>
          <TabsTrigger variant="underline" value="security">Security</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <div className="space-y-6">
            <Card>
              <CardContent className="flex items-center gap-5 pt-6">
                <Avatar size="xl">
                  <AvatarImage src={user?.avatar || getUserAvatarUrl(user?.name, user?.email)} alt={user?.name || 'User'} />
                  <AvatarFallback className="bg-primary text-xl text-primary-foreground">
                    {initials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{user?.name || 'User'}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(user?.roles ?? []).map((r) => (
                      <Badge key={r} variant="primary">{r}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {loadingProfile ? (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading your details…
              </div>
            ) : profile ? (
              <PersonalDetailsForm profile={profile} canEdit includeBasic onSave={saveDetails} />
            ) : (
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <Field label="Full name" htmlFor="name">
                    <Input id="name" value={user?.name ?? ''} disabled />
                  </Field>
                  <Field label="Email address" htmlFor="email" hint="Contact an admin to change your email.">
                    <Input id="email" type="email" value={user?.email ?? ''} disabled />
                  </Field>
                  <p className="text-xs text-muted-foreground">Your detailed employee profile isn’t set up yet. Contact HR to link your account to an employee record.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Preferences */}
        <TabsContent value="preferences">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>Choose how TeamOne looks to you.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 sm:max-w-md">
                  {([
                    { key: 'light', label: 'Light', icon: Sun },
                    { key: 'dark', label: 'Dark', icon: Moon },
                  ] as const).map(({ key, label, icon: Icon }) => {
                    const active = theme === key;
                    return (
                      <Button variant="unstyled" size="unstyled"
                        key={key}
                        onClick={() => setTheme(key)}
                        className={`flex items-center gap-3 rounded-lg border p-4 text-left transition-all ${
                          active
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                            : 'border-border hover:border-border hover:bg-accent/40'
                        }`}
                      >
                        <span className={`flex size-9 items-center justify-center rounded-lg ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                          <Icon className="size-[18px]" />
                        </span>
                        <span className="text-sm font-medium">{label}</span>
                        {active && <Check className="ml-auto size-4 text-primary" />}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security">
          <SecuritySection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Set or change the account password. Someone who only signed in with Google has
 * no password yet (`hasPassword` false); they can set one here and afterwards
 * also sign in manually with their email + this password. Changing an existing
 * password requires the current one.
 */
function SecuritySection() {
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get('/auth/me')
      .then((r) => setHasPassword(!!(r.data?.user?.hasPassword)))
      .catch(() => setHasPassword(null));
  }, []);

  const needsCurrent = hasPassword === true;

  const submit = async () => {
    if (next.length < 8) { toast.error('Your new password must be at least 8 characters.'); return; }
    if (next !== confirm) { toast.error('The new passwords do not match.'); return; }
    if (needsCurrent && !current) { toast.error('Enter your current password.'); return; }
    setSaving(true);
    try {
      const r = await apiClient.post('/auth/set-password', {
        currentPassword: needsCurrent ? current : undefined,
        newPassword: next,
      });
      toast.success(r.data?.message || 'Password updated.');
      setCurrent(''); setNext(''); setConfirm(''); setHasPassword(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not update your password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{needsCurrent ? 'Change password' : 'Set a password'}</CardTitle>
        <CardDescription>
          {hasPassword === false
            ? 'You sign in with Google. Set a password to also sign in manually with your email.'
            : 'Keep your account secure.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {needsCurrent && (
            <>
              <Field label="Current password" htmlFor="cpw">
                <Input id="cpw" type="password" placeholder="••••••••" autoComplete="current-password"
                  value={current} onChange={(e) => setCurrent(e.target.value)} />
              </Field>
              <div />
            </>
          )}
          <Field label="New password" htmlFor="npw" hint="At least 8 characters.">
            <Input id="npw" type="password" placeholder="••••••••" autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label="Confirm new password" htmlFor="npw2">
            <Input id="npw2" type="password" placeholder="••••••••" autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
        </div>
      </CardContent>
      <Separator />
      <CardFooter className="justify-end pt-6">
        <Button onClick={submit} disabled={saving || hasPassword === null}>
          {needsCurrent ? 'Update password' : 'Set password'}
        </Button>
      </CardFooter>
    </Card>
  );
}
